import { Box, Stack } from '@mui/system'
import React from 'react'

import CrossIcon from '@/assets/cross-thick.svg'
import Button, { IProps as IButtonProps } from '@/atoms/Button/Button'
import { ModalVariant } from '@/atoms/Modal/Modal.types'
import { SvgIcon } from '@/atoms/SvgIcon'
import { Circle, Square } from '@/atoms/System'
import { filterEmptyValues } from '@/lib/array-utils'
import { E2E, passTestId } from '@/lib/e2e-utils'
import { useTranslation } from '@/lib/i18n/i18n'
import { CheckCurvyIcon } from '@/organisms/FlightSearchNew/Icons'
import { brightRed, brightTurquoise, fontBlack, white } from '@/refactor/colors'
import { font } from '@/refactor/fonts'
import styled, { margin } from '@/styles'
import { DefaultProps } from '@/travelsuit'

import { ButtonLoader } from '../Loader'
import Modal, { DOMTreePosition, IProps as ModalProps } from '../Modal/Modal'

export interface ModalButtonProps {
	buttonProps?: {
		okButton?: IButtonProps
		cancelButton?: IButtonProps
	}
}
interface IProps extends DefaultProps, ModalButtonProps, E2E {
	title: React.ReactNode
	prompt?: React.ReactNode
	variant?: ModalProps['variant']
	okLabel?: React.ReactNode
	cancelLabel?: React.ReactNode
	width?: number | string
	desktopMaxHeight?: string
	onOK?: (e: React.MouseEvent<HTMLElement>) => void
	onCancel?: (e: React.MouseEvent<HTMLElement>) => void
	/** If not provided onCancel or onOK is used */
	onClose?: (e: React.MouseEvent<HTMLElement>) => void
	domTreePosition?: DOMTreePosition
	open?: boolean
	showCancelButton?: boolean
	zIndex?: number
	buttonWidth?: string
	containerClassName?: string
	isSubmitting?: boolean
}

const Content = styled.div`
	${font({ size: 14 })}
	color: ${fontBlack};
`

const ChildrenContainer = styled.div`
	&:not(:first-child) {
		${margin.symmetric({ vertical: 10 })}
	}
`

const Confirmation: React.FunctionComponent<IProps> = (props) => {
	const { t } = useTranslation()
	const {
		className,
		open,
		domTreePosition,
		children,
		prompt,
		onCancel,
		onOK,
		onClose,
		variant,
		title,
		width,
		desktopMaxHeight,
		showCancelButton = true,
		cancelLabel = t('confirmation.cancel'),
		okLabel = t('confirmation.ok'),
		// KILLME: BCDTDRCT-1400 Drawer backdrop zIndex is 10000
		// KILLME: Sidebar zIndex is 100000
		zIndex = 100005,
		buttonWidth,
		containerClassName,
		isSubmitting,
	} = props

	return (
		<Modal
			data-test={passTestId(props)}
			className={className}
			open={open}
			title={title}
			onClose={onClose ?? onCancel ?? onOK}
			variant={variant}
			width={width}
			desktopMaxHeight={desktopMaxHeight}
			domTreePosition={domTreePosition}
			zIndex={zIndex}
			containerClassName={containerClassName}
			controlButtons={
				onOK || (showCancelButton && onCancel)
					? filterEmptyValues([
							showCancelButton && onCancel ? (
								<Button
									data-test="Confirmation.Button.Cancel"
									key="cancel"
									width={buttonWidth || 'auto'}
									color="secondary"
									onClick={onCancel}
									{...props.buttonProps?.cancelButton}
									disabled={isSubmitting || props.buttonProps?.cancelButton?.disabled}
								>
									{cancelLabel}
								</Button>
							) : null,
							onOK ? (
								<Button
									data-test="Confirmation.Button.Confirm"
									key="ok"
									width={buttonWidth || 'auto'}
									minWidth={showCancelButton && onCancel ? undefined : 160}
									color={variant === 'alert' ? 'negative' : 'bluePrimary'}
									onClick={onOK}
									{...props.buttonProps?.okButton}
									disabled={isSubmitting || props.buttonProps?.okButton?.disabled}
								>
									{isSubmitting ? <ButtonLoader color="contrast" /> : okLabel}
								</Button>
							) : null,
						])
					: []
			}
		>
			<Content data-test="Confirmation.Content">
				{prompt}

				{children ? <ChildrenContainer>{children}</ChildrenContainer> : null}
			</Content>
		</Modal>
	)
}

export default Confirmation

export function ConfirmationWithIcon({
	variant,
	title,
	children,
	onConfirm,
	e2e,
}: {
	variant: Extract<ModalVariant, 'success' | 'alert'>
	title: React.ReactNode
	children: React.ReactNode
	onConfirm: () => void
} & E2E) {
	const icons = {
		success: (
			<Circle size={60} bgcolor={brightTurquoise}>
				<CheckCurvyIcon color={white} size={32} />
			</Circle>
		),
		alert: (
			<Circle size={60} bgcolor={brightRed}>
				<SvgIcon src={CrossIcon} color={white} size={32} />
			</Circle>
		),
	}

	const overlayColor = {
		success: '#CCF1EE',
		alert: '#FFE5E5',
	}
	return (
		<Confirmation title={title} variant={variant} prompt="" onOK={onConfirm} e2e={e2e}>
			<Stack direction="row" spacing={{ xs: 2, lg: 4 }}>
				<Square borderRadius={6} size={100} bgcolor={overlayColor[variant]} display={{ xs: 'none', lg: 'flex' }}>
					{icons[variant]}
				</Square>
				<Box flexGrow={1}>{children}</Box>
			</Stack>
		</Confirmation>
	)
}
